import omit from 'lodash/omit';
import { type DiagramTransition, toPortKey } from './toPortInfo.tsx';
import { isDirectOrSharedTransitionWithDefinedPorts } from './isEveryPropertyDefined.tsx';
import {
	addToOverlapsGroup,
	overlapKeyByPorts,
	type OverlapItem,
	type OverlapKey,
	type OverlapPlacement,
} from './getLabelPlacement.tsx';

/**
 * Returns an array of `OverlapPlacement`s that represent transitions which overlap (based on the type of transition)
 * with a `placement` value between 0 and 1 that represents the fractional position on where to place a label on a
 * transition's path.
 *
 * Example of placement values if there are five transitions overlapping, their values would look like 0, 0.25, 0.5, 0.75, 1
 * If there is no overlap, the result will not contain that transitionId
 *
 * Shared transitions have an additional linkIndex to differentiate between the connections that make up the
 * shared transition.
 *
 * **Current implementation notes**
 * 1. Group transitions (and each shared transition connection) by an overlap criteria based on transition type
 *   a. Additionally take note if overlap exists in reverse
 * 2. Aggregate grouped transitions with a placement value based on position in group
 *   a. determine placement as fraction of number of overlapping items, e.g. 2 overlaps = 0, 1; 5 overlaps = 0, 0.25, 0.5, 0.75, 1
 *   b. if overlap was reversed, instead place on opposite side (1 - value), e.g. 0 -> 1, 1/3 -> 2/3
 *
 * To cheaply determine overlaps - the overlap criteria is flattened into a string representation (e.g. source and target port keys for
 * direct transitions)
 */
export const calculateOverlappingLabelPlacement = (
	transitions: DiagramTransition[],
): OverlapPlacement[] => {
	const groupedByOverlapKey = new Map<OverlapKey, OverlapItem[]>();

	// group transitions by 'overlap key' which is a string comparable represenation of overlap criteria
	transitions.forEach((transition) => {
		if (!isDirectOrSharedTransitionWithDefinedPorts(transition)) {
			return;
		}

		const isSharedTransition = transition.type === 'shared';

		if (isSharedTransition) {
			transition.links.forEach((link, linkIndex) => {
				const startPortKey = toPortKey(link.source, link.sourceIndex);
				const endPortKey = toPortKey(transition.target, link.targetIndex);
				const { overlapKey, isReversed } = overlapKeyByPorts(startPortKey, endPortKey);
				addToOverlapsGroup(groupedByOverlapKey, overlapKey, {
					type: 'shared-transition',
					transitionId: transition.id,
					linkIndex,
					isReversed,
				});
			});
		} else if (transition.type === 'direct') {
			const startPortKey = toPortKey(transition.source, transition.sourceIndex);
			const endPortKey = toPortKey(transition.target, transition.targetIndex);
			const { overlapKey, isReversed } = overlapKeyByPorts(startPortKey, endPortKey);
			addToOverlapsGroup(groupedByOverlapKey, overlapKey, {
				type: 'direct-transition',
				transitionId: transition.id,
				isReversed,
			});
		}
		// }
	}, {});

	const result: OverlapPlacement[] = [];

	// calculate placements for each transition in a group while considering if reversed
	groupedByOverlapKey.forEach((overlaps, overlapKey) => {
		overlaps.forEach((overlap, position) => {
			// by definition a single item in a group has no overlaps, hence '- 1'
			// we only care about overlaps so we ignore all non-overlapping transitions
			const totalOverlapping = overlaps.length - 1;

			if (totalOverlapping === 0) {
				return;
			}

			let placement = position / totalOverlapping;

			if ('isReversed' in overlap && overlap.isReversed) {
				// place transition label in the opposite direction
				placement = 1 - placement;
			}

			result.push({ ...omit(overlap, 'isReversed'), placement, overlapKey });
		});
	});

	return result;
};
